import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import GTM from "../../GTM";
import {
    capitalizeFirstLetter,
    getSelectedIsfOrganizationIds,
    getPresentDayFormat,
    isParameterEnabled,
    getStandardDateFormat,
    getAccessoryKey,
    buildGtmOrgContext,
    filterDocumentDataViews,
    filterSelectedReport,
    getIsNewLeaveChangesEnabled,
    getIsDocumentLibraryTileEnabled,
    getSubViewNames,
    getHighVolumeReport,
    getEmployeeIdByClaimId,
    getEmployeeIdByLeaveId,
} from "../../utils/helpers";
import { Breadcrumb } from "../../components/ui/Breadcrumb";
import ReportFilters from "./ReportFilters";
import ReportViewData from "./ReportViewData";
import {
    paymentHistoryType,
    default_subview_map,
    subview_mapping,
    CONVERGE_TYPE,
    PAYMENT_STATUS,
    STATUS,
    PERIOD_SCHEDULE_TYPE,
    PROTECTION_STATUS,
    PLAN_REQUEST_STATUS,
    INCLUDE_HISTORICAL_LEAVE,
    REPORTING_PERIOD,
    REPORTING_GROUP,
    POLICIES_AND_DIVISIONS,
    CUSTOM_DATE_RANGE,
    DATAVIEWTYPE,
    LEAVE_SCHEDULE_TYPE,
    LEAVE_AS_ACCOMMODATION_REPORT,
    ACCOMMODATION_REPORT,
    APPLY_REPORTING_PERIOD_TO,
    leaveType,
    disabilityType,
    absenceType,
    HIGH_VOLUME_MESSAGES,
    EMPLOYEE_ID,
    EE_ID,
    CLAIM_ID,
    LEAVE_ID,
} from "../../constants";
import moment from "moment";
import FooterNote from "../claimleaveview/FooterNote";
import ReportSaveNotification from "./ReportSaveNotification";
import ReportSaveErrorNotification from "./ReportSaveErrorNotification";
import {
    setReportDetails,
    setSelectedReportViewId as resetSelectedReportViewId,
} from "../../slices/viewReportSlice";
import { useHighVolumeExport } from "./high_Volume/HighVolumeExportHandler";
import ReportDataFetcher from "./ReportDataFetcher";
import {
    useUpdateFrequentlyUsedReportsMutation,
    useSaveReportViewMutation,
    useLazyGetReportDetailsQuery,
    useUpdateReportDetailsMutation,
} from "../../slices/saveReportViewSlice";
import { clearDlUrlValues } from "../../slices/authSlice";
import PropTypes from "prop-types";
import { useFlags } from "launchdarkly-react-client-sdk";
import HighVolumeCard from "./high_Volume/HighVolumeCard.js";
import Loader from "../../components/ui/Loader.js";

function ReportViewPage({ previousUrl }) {
    const applyFilterToObj = (filtersObj, condition, key, value) => {
        if (condition) {
            filtersObj[key] = value;
        }
    };

    const resolveApplyingReportingPeriod = (filter, firstOption) => {
        if (filter.length > 0) return filter;
        return firstOption ? [{ name: firstOption.name, value: firstOption.name }] : [];
    };

    const sortGroupsByDivision = (a, b) => {
        if (a.divisionId !== b.divisionId) {
            return a.divisionId - b.divisionId;
        }
        if (a.divisionName === undefined || b.divisionName === undefined) {
            return;
        }
        return a.divisionName.localeCompare(b.divisionName);
    };

    const processSinglePolicy = (policy, fullListOfPoliciesAndDivisions, unsortedAllowedReportingGroups) => {
        const { policyId, divisionId, divisionName, reportingGroupInd } = policy;
        if (reportingGroupInd === "N") {
            if (!fullListOfPoliciesAndDivisions[policyId]) {
                fullListOfPoliciesAndDivisions[policyId] = { policyId, divisions: [] };
            }
            fullListOfPoliciesAndDivisions[policyId].divisions.push({ divisionId, divisionName });
        }
        if (reportingGroupInd === "Y") {
            unsortedAllowedReportingGroups.push({
                value: policyId + "." + divisionId,
                name: String(+divisionId) + " - " + divisionName,
                divisionId: +divisionId,
                divisionName: divisionName,
            });
        }
    };

    const computePoliciesAndGroups = (portalUserData, selectedIsfOrganizationIds) => {
        if (!portalUserData?.assignedOrgs || !Array.isArray(portalUserData.assignedOrgs)) {
            return { allowedPoliciesAndDivisions: [], allowedReportingGroups: [] };
        }

        const fullListOfPoliciesAndDivisions = {};
        const unsortedAllowedReportingGroups = [];

        portalUserData.assignedOrgs.forEach((assignedOrg) => {
            if (selectedIsfOrganizationIds.includes(assignedOrg.orgId.toLowerCase())) {
                assignedOrg.assignedPolicies.forEach((policy) =>
                    processSinglePolicy(policy, fullListOfPoliciesAndDivisions, unsortedAllowedReportingGroups)
                );
            }
        });

        unsortedAllowedReportingGroups.sort(sortGroupsByDivision);

        return {
            allowedPoliciesAndDivisions: Object.values(fullListOfPoliciesAndDivisions),
            allowedReportingGroups: [...unsortedAllowedReportingGroups],
        };
    };

    const computeHighVolumeThresholds = (maxDisplayRows, totalResults, multiFactorReport) => {
        const hasValidMaxDisplayRows = Number.isFinite(maxDisplayRows) && maxDisplayRows > 0;
        const exceedsHighVolumeThreshold = hasValidMaxDisplayRows && totalResults * multiFactorReport > maxDisplayRows;
        return { hasValidMaxDisplayRows, exceedsHighVolumeThreshold };
    };

    const applyFilterWithTracking = (value) => {
        setOnApplyFilter(value);
        if (value) {
            setFiltersApplied(true);
        }
    };

    const resetFiltersWithTracking = (value) => {
        setResetFilters(value);
        if (value) {
            setFiltersApplied(false);
        }
    };

    const { user, enableSearchHyperlinks } = useSelector((state) => state.auth);
    const { config } = useSelector((state) => state.app);
    const hasTrackedPageView = useRef(false);
    const { viewtype, id } = useParams();
    const childRef = useRef(null);
    const dispatch = useDispatch();
    const {
        displayReports,
        displaySubViewReports,
        displayNewLeaveChangesReport,
        enableHighVolumeExport,
        displayDocumentLibraryTile,
    } = useFlags();

    const { organizations: allOrganizations } = useSelector(
        (state) => state.userDetails
    );

    const isNewLeaveChangesEnabled = getIsNewLeaveChangesEnabled({
        displayNewLeaveChangesReport,
        user,
        allOrganizations,
    });

    const isDocumentLibraryTileEnabled = getIsDocumentLibraryTileEnabled({
        displayDocumentLibraryTile,
        user,
        allOrganizations,
    });
    const [applyFilter, setApplyFilter] = useState(true);
    const [filtersApplied, setFiltersApplied] = useState(false);

    // High volume export handler
    const { handleExport } = useHighVolumeExport();

    const {
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
    } = displaySubViewReports;

    const {
        enableExternalUser: enableHighVolumeExportExternalUser,
        enableInternalUser: enableHighVolumeExportInternalUser,
        enableOrganizationList: enableHighVolumeExportOrganizationList,
        isfUserIds: enableHighVolumeExportIsfUserIds,
        subViewNames: enableHighVolumeExportSubViewNames,
    } = enableHighVolumeExport;

    const isHighVolumeReportResult = getHighVolumeReport({
        enableExternalUser: enableHighVolumeExportExternalUser,
        enableInternalUser: enableHighVolumeExportInternalUser,
        enableOrganizationList: enableHighVolumeExportOrganizationList,
        isfUserIds: enableHighVolumeExportIsfUserIds,
        subViewNames: enableHighVolumeExportSubViewNames,
        user,
    });

    // Memoized values for efficiency
    const gtm = useMemo(() => new GTM(null), []);
    const reportViewType = useMemo(
        () => capitalizeFirstLetter(viewtype),
        [viewtype]
    );
    const pageReferrer = previousUrl?.[previousUrl.length - 1] || "";
    const pageReferrerTitle = pageReferrer.includes("/reportingcapabilities")
        ? "run reports"
        : undefined;

    const [saveReportViewMutation] = useSaveReportViewMutation();
    const [updateReportViewMutation] = useUpdateReportDetailsMutation();

    const [getReportDetails] = useLazyGetReportDetailsQuery({});

    // Organization and report details
    const {
        selectedOrganizationsReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports,
        getReportDetailsFromSlice: allReportViewsData,
        selectedDataViewType: dataViewType,
        selectedReportViewId: initialViewId,
        portalUserData,
    } = useSelector((state) => state.reportOrganization);

    useEffect(() => {
        dispatch(clearDlUrlValues());
        return () => {
            dispatch(resetSelectedReportViewId(null));
        };
    }, []);

    const [updateFrequentlyUsedReportsViewId] =
        useUpdateFrequentlyUsedReportsMutation();

    // get ssn access for selected organization
    const {
        organizationsAccess,
        selectedOrganizations: userSelectedOrganizations,
    } = useSelector((state) => state.organizations);

    const {
        relatedOrganizationNames,
        organizationName,
        selectedIsfOrganizationIds,
        selectedRelatedOrgs,
    } = getSelectedIsfOrganizationIds(
        selectedOrganizationsReports,
        reportId,
        selectedRelatedOrganizationsForReports
    );

    // State for selected report and filters
    const [selectedReportViewId, setSelectedReportViewId] =
        useState(initialViewId);

    const [dropdownSelectedReport, setDropdownSelectedReport] = useState(null);
    const [onApplyFilter, setOnApplyFilter] = useState(
        !isHighVolumeReportResult
    );
    const [highVolumeCardMessage, setHighVolumeCardMessage] = useState(
        isHighVolumeReportResult ? HIGH_VOLUME_MESSAGES.INITIAL : []
    );
    const [filtersVisible, setFiltersVisible] = useState(
        !isHighVolumeReportResult
    );

    const [reportTableData, setReportTableData] = useState([]);
    const [resetFilters, setResetFilters] = useState(false);
    const [reportDescription, setReportDescription] = useState(false);
    const [reportLastFetched, setReportLastFetched] = useState();
    const [disclaimer, setDisclaimer] = useState("");
    const [saveReport, setSaveReport] = useState(false);
    //selected page number
    const [page, setPage] = useState(1);
    const [showReportSaveNotification, setShowReportSaveNotification] =
        useState(false);
    const [notificationMessageIndicator, setNotificationMessageIndicator] =
        useState();
    const [loaderStatus, setLoaderStatus] = useState(false);
    const [leavesStatus, setLeavesStatus] = useState(true);
    const [enableSaveButton, setEnableSaveButton] = useState(false);

    const notificationMessageUpdate = (indicator) => {
        setNotificationMessageIndicator(indicator);
    };

    const handleReportSavedNotificationClose = () => {
        setShowReportSaveNotification(false);
    };
    const createHistoricalLeaveOption = (includeHistoricalLeave) => {
        if (!includeHistoricalLeave) return [];

        return [
            {
                name: includeHistoricalLeave,
                value: includeHistoricalLeave === "No" ? "N" : "Y",
            },
        ];
    };

    const showNotificationWithTimeout = () => {
        setTimeout(() => {
            setShowReportSaveNotification(false);
        }, 6000);
    };

    const employeeIdColumns = [EMPLOYEE_ID, EE_ID];
    const handleNavigateClick = async (keyval, clickval, info) => {
        if (employeeIdColumns.includes(keyval)) {
            window.open(
                `/organization/${selectedIsfOrganizationIds[0]}/employees/${clickval}?ids=${selectedIsfOrganizationIds}`
            );
        }

        if (keyval === LEAVE_ID) {
            let employeeIDS = getEmployeeIdByLeaveId(clickval, info);
            if (/^\d+$/.test(employeeIDS)) {
                window.open(
                    `/organization/${selectedIsfOrganizationIds[0]}/employees/${employeeIDS}?leave=${clickval}&ids=${selectedIsfOrganizationIds}`
                );
            } else {
                window.open(
                    `/organization/${selectedIsfOrganizationIds[0]}/employees/view?leave=${clickval}&ids=${selectedIsfOrganizationIds}`
                );
            }
        }

        if (keyval === CLAIM_ID) {
            let employeeIDS = getEmployeeIdByClaimId(clickval, info);

            if (/^\d+$/.test(employeeIDS)) {
                window.open(
                    `/organization/${selectedIsfOrganizationIds[0]}/employees/${employeeIDS}?claim=${clickval}&ids=${selectedIsfOrganizationIds}`
                );
            } else {
                window.open(
                    `/organization/${selectedIsfOrganizationIds[0]}/employees/view?claim=${clickval}&ids=${selectedIsfOrganizationIds}`
                );
            }
        }
    };

    const [totalResults, setTotalResults] = useState(0); // Store total results count
    const [retrievedCount, setRetrievedCount] = useState(0); // Track fetched records count
    const [isPaginating, setIsPaginating] = useState(false);
    const [isError, setIsError] = useState(false);

    // Derived selected report details
    const selectedReportDetails = useMemo(
        () =>
            allReportViewsData.find(
                (obj) => obj.ViewId === selectedReportViewId
            ) || {},
        [allReportViewsData, selectedReportViewId]
    );

    const {
        Parameters: parameters = {},
        ParametersOptions: reportParametersOptions = [],
        TableDisplayInformation,
    } = selectedReportDetails;

    const [reportTableDisplayInformation, setReportTableDisplayInformation] =
        useState(TableDisplayInformation?.ColumnInformation || []);
    const hyperlinkColumns = [...employeeIdColumns, CLAIM_ID, LEAVE_ID];
    const getCamelCasedReportViewData = (data) => {
        return data?.map((obj) => {
            const accessorKey = getAccessoryKey(
                reportViewType,
                obj.accessorKey
            );

            const isHyperlinkColumn =
                enableSearchHyperlinks &&
                hyperlinkColumns.includes(accessorKey);
            return {
                accessorKey,
                checked: obj.checked,
                enableHiding: !obj.lockedDisplay,
                header: obj.header,
                id: obj.id,
                cell: isHyperlinkColumn
                    ? (info) => {
                        const value = info.getValue();

                        const emptyValues = [
                            "—",
                            "–",
                            "Not Provided",
                            "null",
                            "",
                        ];
                        if (!value || emptyValues.includes(value)) {
                            return value;
                        }

                        return (
                            <span
                                className="report-hyperlink"
                                onClick={() =>
                                    handleNavigateClick(
                                        accessorKey,
                                        value,
                                        info
                                    )
                                }
                            >
                                {value}
                            </span>
                        );
                    }
                    : (info) => info.getValue(),
                size: obj.size,
                minSize: obj.minSize,
                maxSize: obj.maxSize,
                columnId: obj.columnId,
            };
        });
    };

    // Helper functions
    const initializeFilterState = (paramKey) =>
        parameters?.[paramKey]?.map((type) => ({ name: type, value: type })) ||
        [];

    const getOptions = (name) =>
        reportParametersOptions.find((param) => param.parameterName === name)
            ?.options || [];

    const hiddenFilterValues = selectedReportDetails.ParametersOptions?.filter(
        (param) => param.hidden === false && param.enabled === false
    ).map((param) => ({
        parameterName: param.parameterName,
        name: param.options.map((option) => option.name),
    }));

    const getReportingPeriod = useMemo(
        () =>
            parameters?.reportingPeriod === CUSTOM_DATE_RANGE
                ? {
                    name: CUSTOM_DATE_RANGE,
                    displayName: `${CUSTOM_DATE_RANGE} ${getStandardDateFormat(
                        parameters.EffectiveStartDate
                    )} - ${getStandardDateFormat(
                        parameters.EffectiveEndDate
                    )}`,
                    value: {
                        effectiveStartDt: getStandardDateFormat(
                            parameters.EffectiveStartDate
                        ),
                        effectiveEndDt: getStandardDateFormat(
                            parameters.EffectiveEndDate
                        ),
                    },
                }
                : getOptions("Reporting Period").find(
                    (option) => option.name === parameters?.reportingPeriod
                ),
        [parameters, reportParametersOptions]
    );

    // State for individual filters
    const [selectedCoverageType, setSelectedCoverageType] = useState(
        initializeFilterState("CoverageType")
    );
    const [selectedStatus, setSelectedStatus] = useState(
        initializeFilterState("Status")
    );
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(
        initializeFilterState("PaymentStatuses")
    );
    const [selectedPeriodScheduleType, setSelectedPeriodScheduleType] =
        useState(initializeFilterState("PeriodScheduleTypes"));
    const [selectedLeaveScheduleType, setSelectedLeaveScheduleType] = useState(
        reportParametersOptions
            ?.filter((item) => item.parameterName === LEAVE_SCHEDULE_TYPE)
            .map((item) => (item.options.length > 0 ? item.options : []))
            .flat() || []
    );
    const [selectedPeriodStatus, setSelectedPeriodStatus] = useState(
        initializeFilterState("planRequestStatus").length > 0
            ? initializeFilterState("planRequestStatus")
            : getOptions(PLAN_REQUEST_STATUS)
    );

    const [selectedProtectionStatus, setSelectedProtectionStatus] = useState(
        initializeFilterState("ProtectionStatuses")
    );
    const [selectedIncludeHistoricalLeave, setSelectedIncludeHistoricalLeave] =
        useState(
            parameters?.IncludeHistoricalLeave
                ? [
                    {
                        name: parameters.IncludeHistoricalLeave,
                        value:
                            parameters.IncludeHistoricalLeave === "No"
                                ? "N"
                                : "Y",
                    },
                ]
                : []
        );
    const [selectedApplyingReportingPeriod, setApplyingReportingPeriod] =
        useState(() => {
            const initialValue = initializeFilterState(
                "reportingPeriodDateType"
            );
            if (initialValue.length === 0) {
                const firstOption = getOptions(APPLY_REPORTING_PERIOD_TO)[0];
                return firstOption
                    ? [{ name: firstOption.name, value: firstOption.name }]
                    : [];
            }
            return initialValue;
        });

    const [subviewName, setSubviewName] = useState(
        selectedReportDetails.SubViewName
    );
    const subviewMap = useMemo(
        () => subview_mapping[subviewName] || default_subview_map,
        [subviewName]
    );

    const [selectedReportingPeriod, setSelectedReportingPeriod] = useState(
        getReportingPeriod === undefined
            ? getOptions("Reporting Period")[0]
            : getReportingPeriod
    );

    const getCommonDateFilters = (dataType = "default") => {
        if (
            dataType === "claims" &&
            (subviewName === "All Open Absences" ||
                subviewName === "All Open Disability")
        ) {
            return {
                effectiveStartDate: getPresentDayFormat("1900-01-01"),
                effectiveEndDate: getPresentDayFormat("1900-01-01"),
            };
        }

        return {
            effectiveStartDate: getPresentDayFormat(
                selectedReportingPeriod?.value?.effectiveStartDt ||
                getOptions("Reporting Period")[0]?.value?.effectiveStartDt
            ),
            effectiveEndDate: getPresentDayFormat(
                selectedReportingPeriod?.value?.effectiveEndDt ||
                getOptions("Reporting Period")[0]?.value?.effectiveEndDt
            ),
        };
    };

    const validateInitialParams = (value) => {
        if (value?.[0]?.name === "Effective Date" || value?.length === 0) {
            const firstOption = getOptions(APPLY_REPORTING_PERIOD_TO)[0];
            return firstOption
                ? [{ name: firstOption.name, value: firstOption.name }]
                : [];
        }
        return value;
    };

    useEffect(() => {
        if (selectedReportingPeriod === undefined) {
            setSelectedReportingPeriod(
                getOptions(APPLY_REPORTING_PERIOD_TO)[0]
            );
        }
    }, [selectedReportingPeriod]);

    const { allowedPoliciesAndDivisions, allowedReportingGroups } =
        computePoliciesAndGroups(portalUserData, selectedIsfOrganizationIds);

    const initialPolicyDivision = allowedPoliciesAndDivisions.flatMap(
        (item) => [
            item.policyId,
            ...(Array.isArray(item.divisions)
                ? item.divisions.map(
                    (division) => `${item.policyId}-${division.divisionId}`
                )
                : []),
        ]
    );

    // Extract values from filteredReportingGroup
    const filteredValues = initializeFilterState("ReportingGroups").map(
        (obj) => obj.value
    );

    // Find matching objects in allowedReportingGroups
    const matchedGroups = allowedReportingGroups.filter((obj) =>
        filteredValues.includes(obj.value)
    );

    // Saved values may belong to another organization; when none apply, select all
    // options of the current organization (same as a standard report).
    const initialSelectedReportingGroup =
        matchedGroups.length > 0 ? matchedGroups : allowedReportingGroups;

    const matchedPolicyDivisions = initializeFilterState("PolicyAndDivisions")
        .map((obj) => obj.value)
        .filter((val) => initialPolicyDivision.includes(val));
    const initialSelectedPolicyDivision =
        matchedPolicyDivisions.length > 0
            ? matchedPolicyDivisions
            : initialPolicyDivision;

    const enableStatusFilter = isParameterEnabled(
        reportParametersOptions,
        STATUS
    );
    const enablePeriodScheduleTypeFilter = isParameterEnabled(
        reportParametersOptions,
        PERIOD_SCHEDULE_TYPE
    );
    const enableLeaveScheduleTypeFilter = isParameterEnabled(
        reportParametersOptions,
        LEAVE_SCHEDULE_TYPE
    );
    const enablePeriodStatusFilter = isParameterEnabled(
        reportParametersOptions,
        PLAN_REQUEST_STATUS
    );
    const enableProtectionStatusFilter = isParameterEnabled(
        reportParametersOptions,
        PROTECTION_STATUS
    );
    const enableIncludeHistoricalLeave = isParameterEnabled(
        reportParametersOptions,
        INCLUDE_HISTORICAL_LEAVE
    );
    const enableReportingPeriodFilter = isParameterEnabled(
        reportParametersOptions,
        REPORTING_PERIOD
    );
    const enableReportGroupFilter = isParameterEnabled(
        reportParametersOptions,
        REPORTING_GROUP
    );
    const enablePolicyandDivisisonFilter = isParameterEnabled(
        reportParametersOptions,
        POLICIES_AND_DIVISIONS
    );
    const enableCoverageType = isParameterEnabled(
        reportParametersOptions,
        CONVERGE_TYPE
    );
    const enablePaymentStatus = isParameterEnabled(
        reportParametersOptions,
        PAYMENT_STATUS
    );
    const enableApplyReportingPeriodFilter = isParameterEnabled(
        reportParametersOptions,
        APPLY_REPORTING_PERIOD_TO
    );

    const [selectedPolicyDivision, setSelectedPolicyDivision] = useState(
        initialSelectedPolicyDivision
    );

    const [selectedReportingGroup, setSelectedReportingGroup] = useState(
        initialSelectedReportingGroup
    );
    const [isFetchingData, setIsFetchingData] = useState(false);
    const [isFetchingAccommodation, setIsFetchingAccommodation] =
        useState(false);

    // Dropdown options for reports
    const selectedReportTypeView = useMemo(
        () =>
            allReportViewsData.filter((obj) => {
                const matchesViewType =
                    viewtype === paymentHistoryType
                        ? obj.DataViewType.toLowerCase().includes("payment")
                        : obj.DataViewType.includes(viewtype);
                if (!matchesViewType) return false;
                if (
                    obj.SubViewName === "Leave Changes" &&
                    obj.DataViewType === leaveType &&
                    !isNewLeaveChangesEnabled
                ) {
                    return false;
                }

                return true;
            }),
        [allReportViewsData, viewtype, isNewLeaveChangesEnabled]
    );

    const reportDropdownOptions = selectedReportTypeView.map((obj) => ({
        value: obj.Name,
        label: obj.Name,
        key: obj.ViewId,
        ownerType: obj.OwnerType,
        description: obj.Description,
        subViewName: obj.SubViewName,
    }));

    const handleDropdownSelectedReport = (report) => {
        setDropdownSelectedReport(report);
        setSelectedReportViewId(report.key);
        setSaveReport(false);
        setFiltersApplied(false);
        setFiltersVisible(true);

        if (isHighVolumeReportResult) {
            setApplyFilter(true);
            setHighVolumeCardMessage(HIGH_VOLUME_MESSAGES.REVIEW_FILTERS);
        } else {
            setApplyFilter(false);
            setHighVolumeCardMessage([]);
        }
        setResetFilters(true);
    };

    const removeHtmlTags = (content) => {
        const doc = new DOMParser().parseFromString(content, "text/html");
        return doc.body.textContent;
    };

    useEffect(() => {
        if (childRef.current) {
            const span1Content = childRef.current.span1?.innerHTML;
            const span2Content = childRef.current.span2?.innerHTML;
            const span2CleanContent = removeHtmlTags(span2Content);
            const mergingDisclaimerContent =
                span1Content +
                (span2CleanContent !== "undefined" && span2CleanContent !== null
                    ? span2CleanContent
                    : "");
            setDisclaimer(mergingDisclaimerContent);
        }
    }, []);

    // setting values when user select different report
    useEffect(() => {
        if (!selectedReportViewId) return;
        // Update filters based on selected report
        const newSelectedReportDetails =
            allReportViewsData.find(
                (obj) => obj.ViewId === selectedReportViewId
            ) || {};
        const {
            Parameters: newParameters = {},
            ParametersOptions: newReportParametersOptions = [],
        } = newSelectedReportDetails;
        const initializeFilter = (paramKey) =>
            newParameters?.[paramKey]?.map((type) => ({
                name: type,
                value: type,
            })) || [];

        const newReportingPeriod =
            newParameters?.reportingPeriod === CUSTOM_DATE_RANGE
                ? getReportingPeriod
                : getOptions("Reporting Period").find(
                    (option) => option.name === newParameters?.reportingPeriod
                );

        setReportTableDisplayInformation(
            newSelectedReportDetails?.TableDisplayInformation
                ?.ColumnInformation || []
        );
        setIsError(false);
        setSelectedCoverageType(initializeFilter("CoverageType"));
        setSelectedStatus(initializeFilter("Status"));
        setSelectedPaymentStatus(initializeFilter("PaymentStatuses"));
        setSelectedPeriodScheduleType(initializeFilter("PeriodScheduleTypes"));
        setSelectedPeriodStatus(
            initializeFilterState("planRequestStatus").length > 0
                ? initializeFilterState("planRequestStatus")
                : getOptions(PLAN_REQUEST_STATUS)
        );
        setSelectedProtectionStatus(initializeFilter("ProtectionStatuses"));

        // Set applying reporting period with fallback to first option if empty
        const applyingReportingPeriodFilter = initializeFilter("reportingPeriodDateType");
        setApplyingReportingPeriod(
            resolveApplyingReportingPeriod(applyingReportingPeriodFilter, getOptions(APPLY_REPORTING_PERIOD_TO)[0])
        );

        setSelectedIncludeHistoricalLeave(
            newParameters?.IncludeHistoricalLeave
                ? createHistoricalLeaveOption(parameters.IncludeHistoricalLeave)
                : []
        );
        setEnableSaveButton(false);
        setSelectedReportingPeriod(
            getReportingPeriod === undefined
                ? getOptions(REPORTING_PERIOD)[0]
                : newReportingPeriod
        );
        setSelectedPolicyDivision(initialSelectedPolicyDivision);
        setSelectedReportingGroup(initialSelectedReportingGroup);
        setResetFilters(true);
        setFiltersVisible(true);
        setSelectedLeaveScheduleType(
            newReportParametersOptions
                ?.filter((item) => item.parameterName === LEAVE_SCHEDULE_TYPE)
                .map((item) => (item.options.length > 0 ? item.options : []))
                .flat() || []
        );

        setReportDescription(newSelectedReportDetails.Description);
        const selectedDropdownValue = {
            value: newSelectedReportDetails.Name,
            label: newSelectedReportDetails.Name,
            key: newSelectedReportDetails.ViewId,
            ownerType: newSelectedReportDetails.OwnerType,
            description: newSelectedReportDetails.Description,
            subViewName: newSelectedReportDetails.SubViewName,
        };
        setDropdownSelectedReport(selectedDropdownValue);

        setSubviewName(newSelectedReportDetails.SubViewName);
        if (!saveReport && !isHighVolumeReportResult) {
            setOnApplyFilter(true);
            setFiltersVisible(true);
            setApplyFilter(false);
        }
        if (!saveReport) {
            setReportTableData([]);
            setTotalResults(0);
            setRetrievedCount(0);
        }
    }, [selectedReportViewId, allReportViewsData]);

    //calling matrixs api
    useEffect(() => {
        if (selectedReportViewId !== null) {
            updateFrequentlyUsedReportsViewId({
                viewId: selectedReportViewId,
                parametrs: {
                    isfUserId: user.userId,
                    selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                },
            });
        }
    }, [selectedReportViewId]);

    // GTM tracking for page view
    useEffect(() => {
        if (user && !hasTrackedPageView.current) {
            hasTrackedPageView.current = true;
            const { userId, isUserInternal } = user;
            const userEmail = user.userEmail || "";
            const { employerName, relatedOrgNames, organizationGroupId } =
                buildGtmOrgContext(userSelectedOrganizations, reportId);

            gtm.PageView(
                `reports ${reportViewType}`,
                employerName,
                userId || "",
                isUserInternal ? "internal" : "external",
                window.location.href,
                `${config.REACT_APP_BASE_URL}${pageReferrer}`,
                pageReferrerTitle,
                id,
                userEmail,
                relatedOrgNames,
                organizationGroupId,
                displayReports
            );
        }
    }, [user, reportViewType, config, pageReferrer, pageReferrerTitle, gtm]);

    const filterAbsenceLeaveStatusValues = selectedStatus.flatMap(
        ({ name }) => {
            const opt = getOptions(STATUS).find(
                (option) => option.name === name
            );
            return opt?.value?.Leave ?? [];
        }
    );

    const filterAbsencePaymentStatusValues = selectedStatus.flatMap(
        ({ name }) => {
            const opt = getOptions(STATUS).find(
                (option) => option.name === name
            );
            return opt?.value?.ClaimPayments ?? [];
        }
    );

    const filterStatusValues = selectedStatus.flatMap(
        (selected) =>
            getOptions(STATUS).find((option) => option.name === selected.name)
                ?.value || []
    );

    const filterApplyingReportingTypeValues =
        selectedApplyingReportingPeriod.flatMap(
            (selected) =>
                getOptions(APPLY_REPORTING_PERIOD_TO).find(
                    (option) => option.name === selected.name
                )?.value || []
        );

    const filterProtectionStatusValues = selectedProtectionStatus
        .map(({ value: selectedValue }) => {
            const matchedOption = getOptions(PROTECTION_STATUS).find(
                ({ value }) =>
                    Array.isArray(value)
                        ? value.includes(selectedValue)
                        : value === selectedValue
            );
            return matchedOption?.value || [];
        })
        .flat();

    const filterAbsenceClaimStatusValues = selectedStatus.flatMap(
        ({ name }) => {
            const opt = getOptions(STATUS).find(
                (option) => option.name === name
            );
            return opt?.value?.Claim ?? [];
        }
    );

    const filteredPaymentsStatusValues = selectedPaymentStatus
        ?.map(
            (selected) =>
                getOptions(PAYMENT_STATUS).find(
                    (option) => option.name === selected.name
                )?.value || []
        )
        .flat();

    const [showErrorNotification, setShowErrorNotification] = useState(false);

    const handleErrorNotificationClose = () => {
        setShowErrorNotification(false);
    };

    const buildCustomDateRange = (reportingPeriod) => {
        if (
            reportingPeriod?.value?.effectiveStartDt &&
            reportingPeriod?.value?.effectiveEndDt
        ) {
            return {
                effectiveStartDt: getPresentDayFormat(
                    reportingPeriod.value.effectiveStartDt
                ),
                effectiveEndDt: getPresentDayFormat(
                    reportingPeriod.value.effectiveEndDt
                ),
            };
        }
        return {};
    };

    const buildConditionalFilters = () => {
        const filters = {};

        applyFilterToObj(filters, enablePolicyandDivisisonFilter, "policyAndDivisions", selectedPolicyDivision);
        applyFilterToObj(filters, enableIncludeHistoricalLeave, "includeHistoricalLeave", selectedIncludeHistoricalLeave?.[0]?.name);
        applyFilterToObj(filters, enablePaymentStatus, "paymentStatuses", selectedPaymentStatus?.map((item) => item.name) || []);
        applyFilterToObj(filters, enablePeriodScheduleTypeFilter, "periodScheduleTypes", selectedPeriodScheduleType?.map((item) => item.value) || []);
        applyFilterToObj(filters, enableLeaveScheduleTypeFilter, "leaveScheduleTypes", selectedLeaveScheduleType?.map((item) => item.value) || []);
        applyFilterToObj(
            filters,
            enablePeriodStatusFilter || selectedPeriodStatus?.length > 0,
            "planRequestStatus",
            selectedPeriodStatus?.map((item) => item.value) || []
        );
        applyFilterToObj(
            filters,
            enableProtectionStatusFilter || selectedProtectionStatus?.length > 0,
            "protectionStatuses",
            selectedProtectionStatus?.map((obj) => obj.value) || []
        );
        applyFilterToObj(filters, enableReportGroupFilter, "reportingGroups", selectedReportingGroup?.map((item) => item.value) || []);

        if (reportViewType !== "PaymentHistory") {
            filters.statuses = selectedStatus?.map((item) => item.name) || [];
        }

        const reportTypeMap = {
            [disabilityType]: "reportingPeriodDateTypeDisability",
            [leaveType]: "reportingPeriodDateTypeLeave",
            [absenceType]: "reportingPeriodDateTypeAbsence",
        };

        const reportingKey = reportTypeMap[reportViewType];

        if (reportingKey) {
            if (
                enableApplyReportingPeriodFilter ||
                selectedApplyingReportingPeriod?.length > 0
            ) {
                filters[reportingKey] = [
                    selectedApplyingReportingPeriod?.[0]?.name,
                ];
            }
        }

        return filters;
    };

    const buildReportingPeriod = () => {
        return enableReportingPeriodFilter
            ? { reportingPeriod: selectedReportingPeriod?.name }
            : { reportingPeriod: "" };
    };

    const filterResult = getSubViewNames({
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
        user,
    });

    // Final object with reduced complexity
    const saveUpdateReportParameters = {
        coverageTypes: selectedCoverageType.map((item) => item.value),
        ...buildCustomDateRange(selectedReportingPeriod),
        ...buildConditionalFilters(),
        ...buildReportingPeriod(),
    };

    const handleViewReportSave = (
        changedColumnSorting,
        changedColumnOrder,
        viewName,
        viewDescription,
        columnLevelFilters,
        setIsResetButtonEnabled
    ) => {
        setLoaderStatus(true);
        saveReportViewMutation({
            dataToUpdate: {
                isfUserId: user.userId,
                ownerType: "Personal",
                selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                dataViewType:
                    reportViewType === paymentHistoryType
                        ? "Payments"
                        : reportViewType,
                subViewName: subviewName,
                dataViewInfo: {
                    name: viewName,
                    description: viewDescription,
                    parameters: saveUpdateReportParameters,
                    tableDisplayInformation: {
                        sortOrder:
                            changedColumnSorting[0] === undefined
                                ? null
                                : changedColumnSorting[0],

                        columnFilters: columnLevelFilters,
                        columnInformation: changedColumnOrder,
                    },
                },
            },
        })
            .unwrap()
            .then((res) => {
                setSaveReport(true);
                getReportDetails({
                    isfUserId: user.userId,
                    selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                    dataViewType: DATAVIEWTYPE,
                }).then((res) => {
                    setEnableSaveButton(false);
                    dispatch(
                        setReportDetails(
                            filterDocumentDataViews(
                                filterSelectedReport(
                                    res.data,
                                    subViewNames,
                                    filterResult
                                ),
                                isDocumentLibraryTileEnabled
                            )
                        )
                    );

                    //
                    setLoaderStatus(false);
                    setIsResetButtonEnabled(false);
                    setShowReportSaveNotification(true);
                    showNotificationWithTimeout();
                });
                setDropdownSelectedReport({
                    value: viewName,
                    label: viewName,
                    key: res?.viewId,
                    ownerType: "Personal",
                    description: viewDescription,
                    subViewName: subviewName,
                });
                setSelectedReportViewId(res?.viewId);
            })
            .catch(() => {
                setLoaderStatus(false);
                setShowErrorNotification(true);
                setTimeout(() => {
                    setShowErrorNotification(false);
                }, 6000);
            });
    };

    const handleViewReportUpdate = (
        changedColumnSorting,
        changedColumnOrder,
        updateViewDetails,
        viewName,
        viewDescription,
        columnLevelFilters,
        setIsResetButtonEnabled
    ) => {
        setLoaderStatus(true);
        updateReportViewMutation({
            dataToUpdate: {
                isfUserId: user.userId,
                ownerType: "Personal",
                selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                dataViewType: updateViewDetails.DataViewType,
                subViewName: subviewName,
                isEnabled: true,
                dataViewInfo: {
                    name: viewName,
                    description: viewDescription,
                    parameters: saveUpdateReportParameters,
                    tableDisplayInformation: {
                        sortOrder:
                            changedColumnSorting[0] === undefined
                                ? null
                                : changedColumnSorting[0],

                        columnFilters: columnLevelFilters,
                        columnInformation: changedColumnOrder,
                    },
                },
            },
            viewId: updateViewDetails.ViewId,
        })
            .unwrap()
            .then(() => {
                setSaveReport(true);
                getReportDetails({
                    isfUserId: user.userId,
                    selectedIsfOrganizationIds: selectedIsfOrganizationIds,
                    dataViewType: DATAVIEWTYPE,
                }).then((res) => {
                    setLoaderStatus(false);
                    dispatch(
                        setReportDetails(
                            filterDocumentDataViews(
                                filterSelectedReport(
                                    res.data,
                                    subViewNames,
                                    filterResult
                                ),
                                isDocumentLibraryTileEnabled
                            )
                        )
                    );

                    setDropdownSelectedReport({
                        value: viewName,
                        label: viewName,
                        key: res?.viewId,
                        ownerType: "Personal",
                        description: viewDescription,
                        subViewName: subviewName,
                    });
                    setIsResetButtonEnabled(false);
                    setShowReportSaveNotification(true);
                    showNotificationWithTimeout();
                });
                setSelectedReportViewId(updateViewDetails.ViewId);
            })

            .catch(() => {
                setLoaderStatus(false);
                setShowErrorNotification(true);
                setTimeout(() => {
                    setShowErrorNotification(false);
                }, 6000);
            });
    };

    // setting reporting fetched date
    useEffect(() => {
        setReportLastFetched(moment().format("MM/DD/YYYY hh:mm A"));
    }, [isFetchingData]);

    useEffect(() => {
        const isAccommodationReport =
            subviewName === LEAVE_AS_ACCOMMODATION_REPORT ||
            subviewName === ACCOMMODATION_REPORT;
        if (!isAccommodationReport) return;
        setIsFetchingAccommodation(
            reportTableData.length === 0 && (isFetchingData || isPaginating)
        );
    }, [isFetchingData, isPaginating, reportTableData.length, subviewName]);

    const multiFactorReport = selectedReportDetails?.ExpansionFactor || 1;
    const maxDisplayRows = selectedReportDetails?.MaxDisplayRows;
    const hasDropdownSelection = Boolean(dropdownSelectedReport);
    const { exceedsHighVolumeThreshold } =
        computeHighVolumeThresholds(maxDisplayRows, totalResults, multiFactorReport);
    const isHighVolumeSubViewEnabled =
        enableHighVolumeExportSubViewNames?.includes(
            dropdownSelectedReport?.subViewName
        );

    const isHighVolumeReport =
        isHighVolumeReportResult &&
        hasDropdownSelection &&
        isHighVolumeSubViewEnabled &&
        exceedsHighVolumeThreshold;

    useEffect(() => {
        if (isHighVolumeReport && filtersApplied) {
            setHighVolumeCardMessage(HIGH_VOLUME_MESSAGES.HIGH_VOLUME);
        }
    }, [isHighVolumeReport, filtersApplied]);

    const handleHighVolumeExport = async () => {
        setResetFilters(true);
        setSelectedReportViewId(null);
        await handleExport({
            dropdownSelectedReport,
            selectedIsfOrganizationIds,
            dataViewType,
            selectedReportViewId,
            userDetails: user,
            setHighVolumeCardMessage,
            setFiltersVisible,
            setDropdownSelectedReport,
            saveUpdateReportParameters,
            filterStatusValues,
            filterApplyingReportingTypeValues,
            filterProtectionStatusValues,
            filterAbsenceClaimStatusValues,
            filterAbsenceLeaveStatusValues,
            filterAbsencePaymentStatusValues,
            filteredPaymentsStatusValues,
            selectedReportingGroup,
            selectedOrganizations: selectedOrganizationsReports[reportId],
            selectedRelatedOrgs,
            enableReportGroupFilter,
            getOptions,
            subviewMap,
            getCommonDateFilters,
        });
    };

    const divisionOnlyForApi = selectedPolicyDivision.filter((item) =>
        /^\d+-\d+$/.test(item)
    );

    const viewNameMap = {
        Payments: "Payment",
        Disability: "Wage Replacement",
        Documents: "Document Library",
    };

    let reportName =
        dataViewType === paymentHistoryType
            ? "Payment"
            : viewNameMap[dataViewType] || dataViewType;

    return (
        <>
            <Breadcrumb
                links={[
                    { path: "/reportingcapabilities", name: "Reports" },
                    {
                        name: reportName,
                    },
                ]}
                setOnApplyFilter={setOnApplyFilter}
            />
            <Helmet>
                <title>Reports | {viewtype}</title>
            </Helmet>

            {/* Visually hidden H1 for accessibility */}
            <h1 className="sr-only">{reportName || "Report"}</h1>

            <ReportFilters
                isHighVolumeReportResult={isHighVolumeReportResult}
                organizationName={organizationName}
                userSelectedOrganizationReports={selectedOrganizationsReports}
                reportId={reportId}
                dataViewType={dataViewType}
                isFetchingReportData={isFetchingData}
                isPaginating={isPaginating}
                relatedOrganizationNames={relatedOrganizationNames}
                reportParametersOptions={reportParametersOptions}
                enableCoverageType={enableCoverageType}
                enableStatusFilter={enableStatusFilter}
                enablePaymentStatus={enablePaymentStatus}
                enablePeriodScheduleTypeFilter={enablePeriodScheduleTypeFilter}
                enablePeriodStatusFilter={enablePeriodStatusFilter}
                enableProtectionStatusFilter={enableProtectionStatusFilter}
                enableIncludeHistoricalLeave={enableIncludeHistoricalLeave}
                enableReportingPeriodFilter={enableReportingPeriodFilter}
                enableReportGroupFilter={enableReportGroupFilter}
                enablePolicyandDivisisonFilter={enablePolicyandDivisisonFilter}
                allowedPoliciesAndDivisions={allowedPoliciesAndDivisions}
                reportingGroup={allowedReportingGroups}
                setSelectedReportingGroup={setSelectedReportingGroup}
                //
                initialPolicyDivision={initialSelectedPolicyDivision}
                selectedCoverageType={selectedCoverageType}
                selectedReportingGroup={selectedReportingGroup}
                selectedPeriodScheduleType={selectedPeriodScheduleType}
                selectedStatus={selectedStatus}
                selectedPaymentStatus={selectedPaymentStatus}
                selectedPeriodStatus={selectedPeriodStatus}
                selectedProtectionStatus={selectedProtectionStatus}
                selectedIncludeHistoricalLeave={selectedIncludeHistoricalLeave}
                selectedReportingPeriod={selectedReportingPeriod}
                selectedPolicyDivision={selectedPolicyDivision}
                setSelectedCoverageType={setSelectedCoverageType}
                setSelectedPeriodScheduleType={setSelectedPeriodScheduleType}
                setSelectedStatus={setSelectedStatus}
                setSelectedPaymentStatus={setSelectedPaymentStatus}
                setSelectedPeriodStatus={setSelectedPeriodStatus}
                setSelectedProtectionStatus={setSelectedProtectionStatus}
                setSelectedIncludeHistoricalLeave={
                    setSelectedIncludeHistoricalLeave
                }
                setSelectedReportingPeriod={setSelectedReportingPeriod}
                setSelectedPolicyDivision={setSelectedPolicyDivision}
                setOnApplyFilter={applyFilterWithTracking}
                onApplyFilter={onApplyFilter}
                setEnableSaveButton={setEnableSaveButton}
                initialReportingPeriod={getReportingPeriod}
                resetFilters={resetFilters}
                setResetFilters={resetFiltersWithTracking}
                hiddenFilterValues={hiddenFilterValues}
                setTotalResults={setTotalResults}
                setRetrievedCount={setRetrievedCount}
                initialCoverageFilter={initializeFilterState("CoverageType")}
                initialStatusFilter={initializeFilterState("Status")}
                initialPaymentStatus={initializeFilterState("PaymentStatuses")}
                initialPeriodScheduleTypes={initializeFilterState(
                    "PeriodScheduleTypes"
                )}
                initialPeriodStatuses={
                    initializeFilterState("planRequestStatus").length > 0
                        ? initializeFilterState("planRequestStatus")
                        : getOptions(PLAN_REQUEST_STATUS)
                }
                initialProtectionStatuses={initializeFilterState(
                    "ProtectionStatuses"
                )}
                initialReportingGroup={initialSelectedReportingGroup}
                initialIncludeHistoricalLeave={createHistoricalLeaveOption(
                    parameters?.IncludeHistoricalLeave
                )}
                dropdownSelectedReport={dropdownSelectedReport}
                reportViewType={reportViewType}
                subviewName={subviewName}
                enableApplyReportingPeriodFilter={
                    enableApplyReportingPeriodFilter
                }
                selectedApplyingReportingPeriod={
                    selectedApplyingReportingPeriod
                }
                setApplyingReportingPeriod={setApplyingReportingPeriod}
                initialApplyingReportingPeriodFilter={validateInitialParams(
                    initializeFilterState("reportingPeriodDateType")
                )}
                reportDropdownOptions={reportDropdownOptions}
                handleDropdownSelectedReport={handleDropdownSelectedReport}
                viewtype={reportName}
                applyFilter={applyFilter}
                setApplyFilter={setApplyFilter}
                filtersVisible={filtersVisible}
                setFiltersVisible={setFiltersVisible}
                setHighVolumeCardMessage={setHighVolumeCardMessage}
                highVolumeMessages={HIGH_VOLUME_MESSAGES}
            />

            {/* Include the new component for API calls */}
            <ReportDataFetcher
                isHighVolumeReportResult={isHighVolumeReportResult}
                reportViewType={reportViewType}
                subviewName={subviewName}
                selectedIsfOrganizationIds={selectedIsfOrganizationIds}
                selectedCoverageType={selectedCoverageType}
                selectedStatus={selectedStatus}
                selectedPaymentStatus={selectedPaymentStatus}
                selectedPeriodScheduleType={selectedPeriodScheduleType}
                selectedPeriodStatus={selectedPeriodStatus}
                selectedProtectionStatus={selectedProtectionStatus}
                selectedIncludeHistoricalLeave={selectedIncludeHistoricalLeave}
                selectedReportingPeriod={selectedReportingPeriod}
                selectedPolicyDivision={divisionOnlyForApi}
                selectedReportingGroup={selectedReportingGroup}
                subviewMap={subviewMap}
                setReportTableData={setReportTableData}
                onApplyFilter={onApplyFilter}
                setOnApplyFilter={setOnApplyFilter}
                setIsFetchingData={setIsFetchingData}
                setIsError={setIsError}
                getCommonDateFilters={getCommonDateFilters}
                dataViewType={reportViewType}
                setTotalResults={setTotalResults}
                setRetrievedCount={setRetrievedCount}
                setIsPaginating={setIsPaginating}
                filterStatusValues={filterStatusValues}
                filterAbsenceLeaveStatusValues={filterAbsenceLeaveStatusValues}
                filterAbsencePaymentStatusValues={
                    filterAbsencePaymentStatusValues
                }
                filterAbsenceClaimStatusValues={filterAbsenceClaimStatusValues}
                filteredPaymentsStatusValues={filteredPaymentsStatusValues}
                reportTableDisplayInformation={reportTableDisplayInformation}
                selectedLeaveScheduleType={selectedLeaveScheduleType}
                setLoaderStatus={setLeavesStatus}
                filterProtectionStatusValues={filterProtectionStatusValues}
                filterApplyingReportingTypeValues={
                    filterApplyingReportingTypeValues
                }
                dropdownSelectedReport={dropdownSelectedReport}
                user={user}
                multiFactorReport={multiFactorReport}
                maxDisplayRows={maxDisplayRows}
                reportDropdownOptions={reportDropdownOptions}
                filtersVisible={filtersVisible}
                setFiltersVisible={setFiltersVisible}
                setHighVolumeCardMessage={setHighVolumeCardMessage}
            />

            {highVolumeCardMessage.length > 0 && (
                <HighVolumeCard
                    messages={highVolumeCardMessage}
                    handleHighVolumeExport={handleHighVolumeExport}
                />
            )}

            <div
                style={{
                    display:
                        highVolumeCardMessage.length > 0 ? "none" : "block",
                }}
            >
                <ReportViewData
                    selectedReportDetails={selectedReportDetails}
                    selectedReportTypeView={selectedReportTypeView}
                    dataViewType={reportViewType}
                    organizationName={organizationName}
                    relatedOrganizationNames={relatedOrganizationNames}
                    allReportViewsData={allReportViewsData}
                    setSelectedReportViewId={setSelectedReportViewId}
                    reportTableDisplayInformation={getCamelCasedReportViewData(
                        reportTableDisplayInformation
                    )}
                    handleDropdownSelectedReport={handleDropdownSelectedReport}
                    reportDropdownOptions={reportDropdownOptions}
                    dropdownSelectedReport={dropdownSelectedReport}
                    reportTableData={reportTableData}
                    reportViewInformation={selectedReportTypeView}
                    page={page}
                    setPage={setPage}
                    isFetchingReportData={isFetchingData}
                    isFetchingAccommodation={isFetchingAccommodation}
                    subviewName={subviewName}
                    isErrorReport={isError}
                    organizationsAccess={organizationsAccess[reportId]}
                    reportDescription={reportDescription}
                    reportLastFetched={reportLastFetched}
                    disclaimer={disclaimer}
                    userEmailID={user.userEmail}
                    totalResults={totalResults}
                    retrievedCount={retrievedCount}
                    isPaginating={isPaginating}
                    hiddenFilterValues={hiddenFilterValues}
                    setEnableSaveButton={setEnableSaveButton}
                    //filter values
                    selectedCoverageType={selectedCoverageType}
                    selectedReportingGroup={selectedReportingGroup}
                    selectedPeriodScheduleType={selectedPeriodScheduleType}
                    selectedLeaveScheduleType={selectedLeaveScheduleType}
                    selectedStatus={selectedStatus}
                    selectedPaymentStatus={selectedPaymentStatus}
                    selectedPeriodStatus={selectedPeriodStatus}
                    selectedProtectionStatus={selectedProtectionStatus}
                    selectedIncludeHistoricalLeave={
                        selectedIncludeHistoricalLeave
                    }
                    selectedReportingPeriod={selectedReportingPeriod}
                    selectedPolicyDivision={selectedPolicyDivision}
                    enableCoverageType={enableCoverageType}
                    enableStatusFilter={enableStatusFilter}
                    enablePeriodScheduleTypeFilter={
                        enablePeriodScheduleTypeFilter
                    }
                    enableLeaveScheduleTypeFilter={
                        enableLeaveScheduleTypeFilter
                    }
                    enablePeriodStatusFilter={enablePeriodStatusFilter}
                    enableProtectionStatusFilter={enableProtectionStatusFilter}
                    enableIncludeHistoricalLeave={enableIncludeHistoricalLeave}
                    enableReportingPeriodFilter={enableReportingPeriodFilter}
                    enablePaymentStatus={enablePaymentStatus}
                    enableReportGroupFilter={enableReportGroupFilter}
                    enablePolicyandDivisisonFilter={
                        enablePolicyandDivisisonFilter
                    }
                    handleViewReportSave={handleViewReportSave}
                    loaderStatus={loaderStatus}
                    notificationStatus={notificationMessageUpdate}
                    allViewsData={allReportViewsData}
                    handleViewReportUpdate={handleViewReportUpdate}
                    enableSaveButton={enableSaveButton}
                    leavesStatus={leavesStatus}
                    subviewMap={subviewMap}
                    enableApplyReportingPeriodFilter={
                        enableApplyReportingPeriodFilter
                    }
                    selectedApplyingReportingPeriod={
                        selectedApplyingReportingPeriod
                    }
                    filterApplyingReportingTypeValues={
                        filterApplyingReportingTypeValues
                    }
                    HighVolumeReport={!isHighVolumeReportResult}
                />
            </div>
            {isFetchingData && highVolumeCardMessage.length > 0 && (
                <Loader text="Loading" />
            )}
            {isFetchingData ? (
                <FooterNote date="..." reportViewType={reportViewType} />
            ) : (
                <FooterNote
                    date={reportLastFetched}
                    reportViewType={reportViewType}
                    ref={childRef}
                />
            )}
            {showReportSaveNotification && (
                <ReportSaveNotification
                    onCloseNotification={handleReportSavedNotificationClose}
                    msgIndicator={notificationMessageIndicator}
                ></ReportSaveNotification>
            )}
            {showErrorNotification && (
                <ReportSaveErrorNotification
                    onCloseNotification={handleErrorNotificationClose}
                ></ReportSaveErrorNotification>
            )}
        </>
    );
}

ReportViewPage.propTypes = {
    previousUrl: PropTypes.array,
};

export default ReportViewPage;
